import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { ROLE_LABEL } from "@/lib/api";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Logo, PoweredByMangoCubs } from "@/components/Logo";
import WelcomeMicroDelight from "@/components/WelcomeMicroDelight";
import EnvBadge from "@/components/EnvBadge";
import {
  House, ChalkboardTeacher, ChartLine, Users, Baby, GraduationCap,
  CurrencyCircleDollar, Bell, Calendar, ChatCircleText, ShoppingBag,
  ClipboardText, HeartHalf, ForkKnife, Buildings, Megaphone,
  Sparkle, ChartPie, SignOut, List, MagnifyingGlass, IdentificationCard,
  Gear, NotePencil, ShieldCheck, Path, Heart, Briefcase, Storefront, Confetti,
  BookOpen, SignIn, Palette, Bank,
} from "@phosphor-icons/react";

const NAV_BY_ROLE = {
  super_admin: [
    { to: "/app/admin", labelKey: "dashboard.overview", icon: House },
    { to: "/app/enquiry-analytics", labelKey: "nav.enquiryAnalytics", icon: ChartLine },
    { to: "/app/branches", labelKey: "nav.branches", icon: Buildings },
    { to: "/app/enquiries", labelKey: "nav.enquiries", icon: MagnifyingGlass },
    { to: "/app/admissions", labelKey: "nav.admissions", icon: ClipboardText },
    { to: "/app/students", labelKey: "nav.students", icon: Baby },
    { to: "/app/teachers", labelKey: "nav.teachers", icon: GraduationCap },
    { to: "/app/parents", labelKey: "nav.parents", icon: Users },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
    { to: "/app/hrms", labelKey: "nav.hrms", icon: Briefcase },
    { to: "/app/payroll", labelKey: "nav.payroll", icon: CurrencyCircleDollar },
    { to: "/app/vendors", labelKey: "nav.vendors", icon: Storefront },
    { to: "/app/fees", labelKey: "nav.fees", icon: CurrencyCircleDollar },
    { to: "/app/attendance", labelKey: "nav.attendance", icon: ChalkboardTeacher },
    { to: "/app/pickup", labelKey: "nav.pickup", icon: ShieldCheck },
    { to: "/app/entry-exit", labelKey: "nav.entryExit", icon: SignIn },
    { to: "/app/gate-pass", labelKey: "nav.gatePass", icon: ShieldCheck },
    { to: "/app/authorised-contacts", labelKey: "nav.authorisedContacts", icon: Users },
    { to: "/app/apparel", labelKey: "nav.apparel", icon: ShoppingBag },
    { to: "/app/activities", labelKey: "nav.activities", icon: Confetti },
    { to: "/app/curriculum", labelKey: "nav.curriculum", icon: NotePencil },
    { to: "/app/admin-lms", labelKey: "nav.lmsAdmin", icon: BookOpen },
    { to: "/app/photos", labelKey: "nav.photoStudio", icon: ChalkboardTeacher },
    { to: "/app/behaviour", labelKey: "nav.behaviour", icon: Heart },
    { to: "/app/vaccinations", labelKey: "nav.vaccinations", icon: HeartHalf },
    { to: "/app/digest", labelKey: "nav.weeklyDigest", icon: Bell },
    { to: "/app/digiboard", labelKey: "nav.digiBoard", icon: ChalkboardTeacher },
    { to: "/app/communications", labelKey: "nav.communications", icon: ChatCircleText },
    { to: "/app/health", labelKey: "nav.health", icon: HeartHalf },
    { to: "/app/meals", labelKey: "nav.meals", icon: ForkKnife },
    { to: "/app/marketing", labelKey: "nav.marketing", icon: Megaphone },
    { to: "/app/franchise", labelKey: "nav.franchiseHq", icon: Path },
    { to: "/app/ai", labelKey: "nav.mangocubsAi", icon: Sparkle, brand: true },
    { to: "/app/admin-bot", labelKey: "nav.aiCopilot", icon: Sparkle },
    { to: "/app/reports", labelKey: "nav.reports", icon: ChartPie },
    { to: "/app/branding", labelKey: "nav.branding", icon: Palette },
    { to: "/app/users", labelKey: "nav.userManagement", icon: IdentificationCard },
    { to: "/app/admin/verification-queue", label: "Verification Queue", icon: ShieldCheck },
    { to: "/app/admin/verification-demo", label: "Verification Demo", icon: ShieldCheck },
    { to: "/app/admin/whatsapp-diag", label: "WhatsApp Diag", icon: ShieldCheck },
    { to: "/app/serial-and-qr", labelKey: "nav.serialAndQr", icon: ShieldCheck },
    { to: "/app/feature-toggles", labelKey: "nav.featureToggles", icon: ShieldCheck },
    { to: "/app/finance-ops", labelKey: "nav.financeOps", icon: Bank },
    { to: "/app/settings", labelKey: "nav.settings", icon: Gear },
  ],
  hq_admin: [
    { to: "/app/franchise-hq", labelKey: "dashboard.overview", icon: House },
    { to: "/app/enquiry-analytics", labelKey: "nav.enquiryAnalytics", icon: ChartLine },
    { to: "/app/branches", labelKey: "nav.branches", icon: Buildings },
    { to: "/app/franchise", labelKey: "nav.franchiseHq", icon: Path },
    { to: "/app/enquiries", labelKey: "nav.enquiries", icon: MagnifyingGlass },
    { to: "/app/admissions", labelKey: "nav.admissions", icon: ClipboardText },
    { to: "/app/hrms", labelKey: "nav.hrms", icon: Briefcase },
    { to: "/app/vendors", labelKey: "nav.vendors", icon: Storefront },
    { to: "/app/fees", labelKey: "nav.fees", icon: CurrencyCircleDollar },
    { to: "/app/activities", labelKey: "nav.activities", icon: Confetti },
    { to: "/app/curriculum", labelKey: "nav.curriculum", icon: NotePencil },
    { to: "/app/admin-lms", labelKey: "nav.lmsAdmin", icon: BookOpen },
    { to: "/app/marketing", labelKey: "nav.marketing", icon: Megaphone },
    { to: "/app/ai", labelKey: "nav.mangocubsAi", icon: Sparkle, brand: true },
    { to: "/app/admin-bot", labelKey: "nav.aiCopilot", icon: Sparkle },
    { to: "/app/reports", labelKey: "nav.reports", icon: ChartPie },
    { to: "/app/users", labelKey: "nav.userManagement", icon: IdentificationCard },
    { to: "/app/admin/verification-queue", label: "Verification Queue", icon: ShieldCheck },
    { to: "/app/serial-and-qr", labelKey: "nav.serialAndQr", icon: ShieldCheck },
    { to: "/app/feature-toggles", labelKey: "nav.featureToggles", icon: ShieldCheck },
    { to: "/app/finance-ops", labelKey: "nav.financeOps", icon: Bank },
    { to: "/app/settings", labelKey: "nav.settings", icon: Gear },
  ],
  branch_admin: [
    { to: "/app/branch", labelKey: "dashboard.overview", icon: House },
    { to: "/app/enquiry-analytics", labelKey: "nav.enquiryAnalytics", icon: ChartLine },
    { to: "/app/enquiries", labelKey: "nav.enquiries", icon: MagnifyingGlass },
    { to: "/app/admissions", labelKey: "nav.admissions", icon: ClipboardText },
    { to: "/app/students", labelKey: "nav.students", icon: Baby },
    { to: "/app/teachers", labelKey: "nav.teachers", icon: GraduationCap },
    { to: "/app/parents", labelKey: "nav.parents", icon: Users },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
    { to: "/app/hrms", labelKey: "nav.hrms", icon: Briefcase },
    { to: "/app/payroll", labelKey: "nav.payroll", icon: CurrencyCircleDollar },
    { to: "/app/vendors", labelKey: "nav.vendors", icon: Storefront },
    { to: "/app/fees", labelKey: "nav.fees", icon: CurrencyCircleDollar },
    { to: "/app/attendance", labelKey: "nav.attendance", icon: ChalkboardTeacher },
    { to: "/app/pickup", labelKey: "nav.pickup", icon: ShieldCheck },
    { to: "/app/entry-exit", labelKey: "nav.entryExit", icon: SignIn },
    { to: "/app/gate-pass", labelKey: "nav.gatePass", icon: ShieldCheck },
    { to: "/app/authorised-contacts", labelKey: "nav.authorisedContacts", icon: Users },
    { to: "/app/apparel", labelKey: "nav.apparel", icon: ShoppingBag },
    { to: "/app/activities", labelKey: "nav.activities", icon: Confetti },
    { to: "/app/curriculum", labelKey: "nav.curriculum", icon: NotePencil },
    { to: "/app/admin-lms", labelKey: "nav.lmsAdmin", icon: BookOpen },
    { to: "/app/photos", labelKey: "nav.photoStudio", icon: ChalkboardTeacher },
    { to: "/app/behaviour", labelKey: "nav.behaviour", icon: Heart },
    { to: "/app/vaccinations", labelKey: "nav.vaccinations", icon: HeartHalf },
    { to: "/app/digest", labelKey: "nav.weeklyDigest", icon: Bell },
    { to: "/app/communications", labelKey: "nav.communications", icon: ChatCircleText },
    { to: "/app/health", labelKey: "nav.health", icon: HeartHalf },
    { to: "/app/meals", labelKey: "nav.meals", icon: ForkKnife },
    { to: "/app/ai", labelKey: "nav.mangocubsAi", icon: Sparkle, brand: true },
    { to: "/app/admin-bot", labelKey: "nav.aiCopilot", icon: Sparkle },
    { to: "/app/reports", labelKey: "nav.reports", icon: ChartPie },
    { to: "/app/branding", labelKey: "nav.branding", icon: Palette },
    { to: "/app/users", labelKey: "nav.userManagement", icon: IdentificationCard },
    { to: "/app/serial-and-qr", labelKey: "nav.serialAndQr", icon: ShieldCheck },
    { to: "/app/feature-toggles", labelKey: "nav.featureToggles", icon: ShieldCheck },
    { to: "/app/finance-ops", labelKey: "nav.financeOps", icon: Bank },
  ],
  teacher: [
    { to: "/app/teacher", labelKey: "nav.myDay", icon: House },
    { to: "/app/my-tasks", labelKey: "nav.myTasks", icon: ClipboardText },
    { to: "/app/students", labelKey: "nav.myClass", icon: Baby },
    { to: "/app/attendance", labelKey: "nav.attendance", icon: ChalkboardTeacher },
    { to: "/app/entry-exit", labelKey: "nav.entryExit", icon: SignIn },
    { to: "/app/gate-pass", labelKey: "nav.gatePass", icon: ShieldCheck },
    { to: "/app/daily-updates", labelKey: "nav.dailyUpdates", icon: NotePencil },
    { to: "/app/behaviour", labelKey: "nav.behaviour", icon: Heart },
    { to: "/app/curriculum", labelKey: "nav.curriculum", icon: NotePencil },
    { to: "/app/lms", labelKey: "nav.lms", icon: BookOpen },
    { to: "/app/teacher-lms", labelKey: "nav.teacherLms", icon: BookOpen },
    { to: "/app/photos", labelKey: "nav.photoStudio", icon: ChalkboardTeacher },
    { to: "/app/health", labelKey: "nav.health", icon: HeartHalf },
    { to: "/app/vaccinations", labelKey: "nav.vaccinations", icon: HeartHalf },
    { to: "/app/digest", labelKey: "nav.weeklyDigest", icon: Bell },
    { to: "/app/activities", labelKey: "nav.activities", icon: Confetti },
    { to: "/app/digiboard", labelKey: "nav.digiBoard", icon: ChalkboardTeacher },
    { to: "/app/communications", labelKey: "nav.parents", icon: ChatCircleText },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
    { to: "/app/ai", labelKey: "nav.mangocubsAi", icon: Sparkle, brand: true },
  ],
  parent: [
    { to: "/app/parent", labelKey: "nav.home", icon: House },
    { to: "/app/timeline", labelKey: "nav.dailyTimeline", icon: ChartLine },
    { to: "/app/parent-entry-exit", labelKey: "nav.arrivalPickup", icon: SignIn },
    { to: "/app/gate-pass", labelKey: "nav.gatePass", icon: ShieldCheck },
    { to: "/app/authorised-contacts", labelKey: "nav.authorisedContacts", icon: Users },
    { to: "/app/parent-digests", labelKey: "nav.weeklyDigest", icon: Bell },
    { to: "/app/parent-attendance", labelKey: "nav.attendance", icon: ChalkboardTeacher },
    { to: "/app/parent-fees", labelKey: "nav.fees", icon: CurrencyCircleDollar },
    { to: "/app/parent-apparel", labelKey: "nav.apparel", icon: ShoppingBag },
    { to: "/app/parent-events", labelKey: "parent.events", icon: Calendar },
    { to: "/app/parent-meals", labelKey: "nav.meals", icon: ForkKnife },
    { to: "/app/parent-notices", labelKey: "parent.notices", icon: Bell },
    { to: "/app/vaccinations", labelKey: "nav.vaccinations", icon: HeartHalf },
    { to: "/app/health", labelKey: "nav.health", icon: HeartHalf },
    { to: "/app/behaviour", labelKey: "nav.behaviour", icon: Heart },
    { to: "/app/parent-learning", labelKey: "nav.learningJourney", icon: BookOpen },
    { to: "/app/communications", labelKey: "nav.messages", icon: ChatCircleText },
    { to: "/app/refer", labelKey: "parent.referFriend", icon: Heart },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
    { to: "/app/my-settings", labelKey: "nav.settings", icon: Gear },
  ],
  counsellor: [
    { to: "/app/counsellor", labelKey: "dashboard.overview", icon: House },
    { to: "/app/enquiry-analytics", labelKey: "nav.myPerformance", icon: ChartLine },
    { to: "/app/enquiries", labelKey: "nav.enquiries", icon: MagnifyingGlass },
    { to: "/app/admissions", labelKey: "nav.admissions", icon: ClipboardText },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
    { to: "/app/marketing", labelKey: "nav.marketing", icon: Megaphone },
    { to: "/app/ai", labelKey: "nav.mangocubsAi", icon: Sparkle, brand: true },
  ],
  auditor: [
    { to: "/app/auditor", labelKey: "nav.complianceFinance", icon: ShieldCheck },
  ],
  non_teaching: [
    { to: "/app/staff", labelKey: "nav.myWorkspace", icon: House },
    { to: "/app/entry-exit", labelKey: "nav.entryExit", icon: SignIn },
    { to: "/app/gate-pass", labelKey: "nav.gatePassScan", icon: ShieldCheck },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
    { to: "/app/communications", labelKey: "nav.messages", icon: ChatCircleText },
  ],
  vendor: [
    { to: "/app/vendor-portal", labelKey: "dashboard.overview", icon: House },
    { to: "/app/vendors", labelKey: "vendor.myOrders", icon: Storefront },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
  ],
  inventory: [
    { to: "/app/inventory-staff", labelKey: "dashboard.overview", icon: House },
    { to: "/app/apparel", labelKey: "nav.apparel", icon: ShoppingBag },
    { to: "/app/vendors", labelKey: "nav.vendors", icon: Storefront },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
  ],
  transport: [
    { to: "/app/transport-staff", labelKey: "dashboard.overview", icon: House },
    { to: "/app/pickup", labelKey: "nav.pickup", icon: ShieldCheck },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
  ],
  support: [
    { to: "/app/support-staff", labelKey: "dashboard.overview", icon: House },
    { to: "/app/tech-support", labelKey: "nav.techSupport", icon: ShieldCheck },
    { to: "/app/communications", labelKey: "nav.communications", icon: ChatCircleText },
    { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
  ],
};

// ----- Wave R1: alias / extended role nav -----
// Family roles (father/mother/guardian) share the parent nav.
NAV_BY_ROLE.father = NAV_BY_ROLE.parent;
NAV_BY_ROLE.mother = NAV_BY_ROLE.parent;
NAV_BY_ROLE.guardian = NAV_BY_ROLE.parent;
// Operational role aliases share the canonical *_staff nav.
NAV_BY_ROLE.non_teaching_staff = NAV_BY_ROLE.non_teaching;
NAV_BY_ROLE.transport_staff = NAV_BY_ROLE.transport;
NAV_BY_ROLE.support_staff = NAV_BY_ROLE.support;
NAV_BY_ROLE.inventory_staff = NAV_BY_ROLE.inventory;
// Activity trainer borrows the teacher nav.
NAV_BY_ROLE.activity_trainer = NAV_BY_ROLE.teacher;
// New Franchise HQ admin uses an enriched HQ nav.
NAV_BY_ROLE.franchise_hq_admin = [
  { to: "/app/franchise-hq", labelKey: "dashboard.overview", icon: House },
  { to: "/app/enquiry-analytics", labelKey: "nav.enquiryAnalytics", icon: ChartLine },
  { to: "/app/branches", labelKey: "nav.branches", icon: Buildings },
  { to: "/app/enquiries", labelKey: "nav.enquiries", icon: MagnifyingGlass },
  { to: "/app/admissions", labelKey: "nav.admissions", icon: ClipboardText },
  { to: "/app/students", labelKey: "nav.students", icon: Baby },
  { to: "/app/teachers", labelKey: "nav.teachers", icon: GraduationCap },
  { to: "/app/parents", labelKey: "nav.parents", icon: Users },
  { to: "/app/hrms", labelKey: "nav.hrms", icon: Briefcase },
  { to: "/app/payroll", labelKey: "nav.payroll", icon: CurrencyCircleDollar },
  { to: "/app/vendors", labelKey: "nav.vendors", icon: Storefront },
  { to: "/app/fees", labelKey: "nav.fees", icon: CurrencyCircleDollar },
  { to: "/app/activities", labelKey: "nav.activities", icon: Confetti },
  { to: "/app/curriculum", labelKey: "nav.curriculum", icon: NotePencil },
  { to: "/app/admin-lms", labelKey: "nav.lmsAdmin", icon: BookOpen },
  { to: "/app/marketing", labelKey: "nav.marketing", icon: Megaphone },
  { to: "/app/tech-support", labelKey: "nav.techSupport", icon: ShieldCheck },
  { to: "/app/ai", labelKey: "nav.mangocubsAi", icon: Sparkle, brand: true },
  { to: "/app/admin-bot", labelKey: "nav.aiCopilot", icon: Sparkle },
  { to: "/app/reports", labelKey: "nav.reports", icon: ChartPie },
  { to: "/app/branding", labelKey: "nav.branding", icon: Palette },
  { to: "/app/users", labelKey: "nav.userManagement", icon: IdentificationCard },
  { to: "/app/serial-and-qr", labelKey: "nav.serialAndQr", icon: ShieldCheck },
  { to: "/app/feature-toggles", labelKey: "nav.featureToggles", icon: ShieldCheck },
  { to: "/app/finance-ops", labelKey: "nav.financeOps", icon: Bank },
  { to: "/app/settings", labelKey: "nav.settings", icon: Gear },
];
// Branch leadership variants of branch_admin.
NAV_BY_ROLE.principal = NAV_BY_ROLE.branch_admin;
NAV_BY_ROLE.academic_coordinator = NAV_BY_ROLE.branch_admin;
// Medical coordinator gets a focused nav.
NAV_BY_ROLE.medical_coordinator = [
  { to: "/app/medical-coord", labelKey: "dashboard.overview", icon: House },
  { to: "/app/students", labelKey: "nav.students", icon: Baby },
  { to: "/app/vaccinations", labelKey: "nav.vaccinations", icon: HeartHalf },
  { to: "/app/health", labelKey: "nav.health", icon: HeartHalf },
  { to: "/app/behaviour", labelKey: "nav.behaviour", icon: Heart },
  { to: "/app/meals", labelKey: "nav.meals", icon: ForkKnife },
  { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
  { to: "/app/communications", labelKey: "nav.communications", icon: ChatCircleText },
];

// Phase 7 — surface the staff self-service hub (My HR / My Payslips)
// as a sidebar link for every non-family role. This is the same
// StaffDashboard page that's been there all along, just made
// discoverable. Family roles (parent/father/mother/guardian) already
// have their own ParentSettings + ParentFees flow.
const STAFF_ROLES_WITH_MY_HR = [
  "teacher", "activity_trainer",
  "counsellor",
  "non_teaching", "non_teaching_staff",
  "transport", "transport_staff",
  "support", "support_staff",
  "inventory", "inventory_staff",
  "medical_coordinator",
  "principal", "academic_coordinator",
  "branch_admin",
  "hq_admin", "franchise_hq_admin",
  "super_admin",
];
const MY_HR_LINK = { to: "/app/my-hr", labelKey: "nav.myHr", icon: Briefcase };
for (const role of STAFF_ROLES_WITH_MY_HR) {
  const nav = NAV_BY_ROLE[role];
  if (!nav || nav.some((x) => x.to === "/app/my-hr")) continue;
  // Slot it right after the dashboard overview so it's always visible
  // near the top, never buried under 15 other items.
  NAV_BY_ROLE[role] = [nav[0], MY_HR_LINK, ...nav.slice(1)];
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { brand, isCoBranded } = useBranding();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, []);

  if (!user) return null;
  // Wave R1: Technical Coordinator gets a dedicated nav even though they're
  // role=non_teaching_staff. Detect via sub_role.
  let items;
  if (user.role === "non_teaching_staff" && user.sub_role === "technical_coordinator") {
    items = [
      { to: "/app/tech-coord", labelKey: "dashboard.overview", icon: House },
      { to: "/app/tech-support", labelKey: "nav.techSupport", icon: ShieldCheck },
      { to: "/app/communications", labelKey: "nav.messages", icon: ChatCircleText },
      { to: "/app/users", labelKey: "nav.userManagement", icon: IdentificationCard },
      { to: "/app/serial-and-qr", labelKey: "nav.serialAndQr", icon: ShieldCheck },
      { to: "/app/documents", labelKey: "nav.documents", icon: ShieldCheck },
      { to: "/app/my-settings", labelKey: "nav.settings", icon: Gear },
    ];
  } else {
    items = NAV_BY_ROLE[user.role] || NAV_BY_ROLE.parent;
  }
  const roleLabel = t(`roles.${user.role}`, { defaultValue: ROLE_LABEL[user.role] || user.role });

  return (
    <div className="min-h-screen bg-paper text-ink flex">
      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-white border-r border-border flex flex-col transition-transform`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-border">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <Logo size={42} tagline={isCoBranded} />
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end
              data-testid={`nav-${it.to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-terracotta text-white shadow-floating"
                    : "text-ink-soft hover:bg-paper-alt hover:text-ink"
                }`
              }
            >
              <it.icon size={18} weight="duotone" />
              <span>{it.brand ? "MangoCubs AI" : (it.label || t(it.labelKey))}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-sage-light text-sage-deep flex items-center justify-center font-semibold text-sm">
            {user.avatar || user.name?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user.name}</div>
            <div className="text-xs text-ink-soft">{roleLabel}</div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="w-9 h-9 rounded-xl hover:bg-paper-alt flex items-center justify-center text-ink-soft"
            data-testid="logout-btn"
            title={t("common.signOut")}
          >
            <SignOut size={18} weight="duotone" />
          </button>
        </div>
        <div className="px-4 pb-4 -mt-1">
          <Link
            to="/app/profile"
            className="flex items-center gap-2 text-xs text-ink-soft hover:text-terracotta transition px-3 py-2 rounded-xl hover:bg-paper-alt"
            data-testid="profile-security-link"
          >
            <ShieldCheck size={14} weight="duotone" />
            <span>My profile & security</span>
          </Link>
        </div>
      </aside>

      {/* Backdrop mobile */}
      {open && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 glass border-b border-border h-16 px-4 lg:px-8 flex items-center gap-3">
          <button
            className="lg:hidden w-10 h-10 rounded-xl hover:bg-paper-alt flex items-center justify-center"
            onClick={() => setOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <List size={20} weight="duotone" />
          </button>
          <div className="flex-1">
            <div className="text-xs text-ink-soft">
              {t("header.welcomeBack")}{isCoBranded && (
                <> · <span className="font-semibold text-ink">{brand.display_name}</span>
                <span className="ml-1.5 text-[10px] tracking-wider text-mustard-hover">Powered by MangoCubs</span></>
              )}
            </div>
            <div className="font-heading font-semibold text-ink leading-none mt-0.5">{user.name?.split(" ")[0] || "Friend"} <span className="text-mustard">✦</span></div>
          </div>
          <Link
            to="/app/notifications"
            className="w-10 h-10 rounded-xl hover:bg-paper-alt flex items-center justify-center relative"
            data-testid="notifications-btn"
          >
            <Bell size={20} weight="duotone" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-terracotta rounded-full" />
          </Link>
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-mustard-light">
            <IdentificationCard size={16} weight="duotone" className="text-mustard-hover" />
            <span className="text-xs font-medium">{roleLabel}</span>
          </div>
          <EnvBadge className="hidden sm:inline-flex" />
        </header>
        {user.must_change_password && (
          <div
            data-testid="must-change-banner-top"
            className="bg-terracotta/10 border-b border-terracotta/30 px-4 lg:px-8 py-3 text-sm flex items-center gap-3"
          >
            <ShieldCheck weight="duotone" className="text-terracotta shrink-0" size={18} />
            <div className="flex-1">
              <span className="font-semibold text-terracotta">Please change your initial password.</span>{" "}
              <span className="text-ink-soft">This is your first sign-in — set a new one to keep your account safe.</span>
            </div>
            <Link
              to="/app/profile"
              className="text-xs font-semibold text-white bg-terracotta hover:bg-terracotta-hover px-3 py-1.5 rounded-full"
              data-testid="must-change-cta"
            >
              Change password →
            </Link>
          </div>
        )}
        <main className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full mx-auto" data-testid="app-main">
          <Outlet />
        </main>
        <footer className="border-t border-border bg-white px-4 lg:px-8 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid="app-footer">
          <div className="text-xs text-ink-soft">
            © {new Date().getFullYear()} {brand?.display_name || "MangoCubs"}
            {isCoBranded && brand?.tagline && <span className="hidden sm:inline"> · {brand.tagline}</span>}
          </div>
          <PoweredByMangoCubs />
        </footer>
      </div>
      <WelcomeMicroDelight />
    </div>
  );
}
